import React from 'react';
import styled from 'styled-components';
import Tooltip from 'components/controls/Tooltip';
import FontIcon from 'components/icons/FontIcon';
import { Skeleton } from 'primereact/skeleton';
import { useDeleteToolVersionMutation } from 'services/api/adminToolsQueriesAndMutations';
import { PatchedToolForAdminModel } from 'types/admin-tools/PatchedToolForAdminModel';
import { ToolForAdminModelVersion } from 'types/admin-tools/ToolForAdminModelVersion';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import { confirmAlert } from 'components/v2/confirm-dialog/confirmAlert';
import { ToolVersionState } from 'types/tools/ToolVersionState';
import { useToolVersionsStats } from 'services/api/toolsQueries';

interface Props {
  toolData: PatchedToolForAdminModel;
  versionData: ToolForAdminModelVersion;
}

const AdminEditToolVersionButtons: React.FC<Props> = ({ toolData, versionData }) => {
  const allStats = useToolVersionsStats(toolData.id).data;
  const versionStats = allStats?.find((v) => v.versionOrd === versionData.ord);
  const deleteVersionMutation = useDeleteToolVersionMutation(toolData.id);

  if (!allStats || !versionStats) {
    return <Skeleton width="5em" height="1em" />;
  }

  const handleDelete = () => {
    if (versionStats.linkedEpdsCount > 0) {
      confirmAlert({
        caption: 'This tool version cannot be removed.',
        description: `${versionStats.linkedEpdsCount} EPDs have been created using this tool version.`,
        infoOnly: true,
        acceptLabel: '',
        onConfirm: null,
      });
      return;
    }

    confirmDelete({
      caption: 'Are you sure you want to remove the tool version?',
      description: 'All filled-in information will be removed.',
      onConfirm: () => deleteVersionMutation.mutate(versionData.ord),
    });
  };

  const getStatusText = (): React.ReactNode => {
    if (versionData.latestStateChangeMessage !== null) {
      return (
        <>
          <div>
            The tool version was {versionData.versionState === ToolVersionState.ENABLED ? 'enabled' : 'disabled'} due to
          </div>
          <UserMessage>"{versionData.latestStateChangeMessage}"</UserMessage>
        </>
      );
    }
    return versionData.versionState === ToolVersionState.ENABLED
      ? 'The tool version is enabled'
      : 'The tool version is disabled';
  };
  return (
    <IconsWrapper>
      <span>
        <i className={versionData.versionState === ToolVersionState.ENABLED ? 'pi pi-eye' : 'pi pi-eye-slash'} />
        <Tooltip position="title">{getStatusText()}</Tooltip>
      </span>

      <IconButton aria-label="Delete version" onClick={handleDelete}>
        <FontIcon icon="delete" />
        <Tooltip position="title">Delete version</Tooltip>
      </IconButton>
    </IconsWrapper>
  );
};

export default AdminEditToolVersionButtons;

const IconsWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  all: unset;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.primaryOrange};
  }
`;

const UserMessage = styled.div`
  font-style: italic;
  max-width: 220px;
  white-space: pre-wrap;
  word-break: break-word;
`;
